import {z} from 'zod';
import {format} from 'date-fns';
import {toast} from 'react-toastify';
import {observer} from 'mobx-react-lite';
import React, {FC, useState} from 'react';
import {zodResolver} from '@hookform/resolvers/zod';
import {VerdocsButton} from '@verdocs/web-sdk-react';
import {FormProvider, useForm, useWatch} from 'react-hook-form';
import {VerdocsEndpoint, setWebhooks, getWebhooks, WEBHOOK_EVENTS, rotateWebhookSecret, TWebhookEvent} from '@verdocs/js-sdk';
import {CheckboxInput, TextInput, Divider, PageTitle, Spinner, FlexFill, HelpDialog, SelectInput} from '../../Components';
import {useOrganizationWebhook} from '../../react-sdk/queries';

type TWebhookEventMap = Record<TWebhookEvent, boolean>;

const EVENTS_AS_OBJECT: Record<string, z.ZodBoolean> = {};
WEBHOOK_EVENTS.forEach((event) => (EVENTS_AS_OBJECT[event] = z.boolean()));

const schema = z
  .object({
    url: z.union([
      z.literal(''),
      z.url({protocol: /^https$/, hostname: z.regexes.domain, message: 'Please enter a valid HTTPS URL.'}).trim(),
    ]),
    active: z.boolean(),
    auth_method: z.enum(['none', 'hmac', 'client_credentials']),
    token_endpoint: z.string().nullish(),
    client_id: z.string().nullish(),
    client_secret: z.string().nullish(),
    scope: z.string().nullish(),
    events: z.object(EVENTS_AS_OBJECT),
  })
  .superRefine((data, ctx) => {
    if (data.auth_method === 'client_credentials') {
      if (!data.client_id) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Client ID is required for Client Credentials authentication.',
          path: ['client_id'],
        });
      }
      if (!data.client_secret) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Client Secret is required for Client Credentials authentication.',
          path: ['client_secret'],
        });
      }
      if (!data.token_endpoint) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Token Endpoint is required for Client Credentials authentication.',
          path: ['token_endpoint'],
        });
      }
    }
  });

type SchemaType = z.infer<typeof schema>;

const StatusBox: FC<{label: string; value: string | null | boolean; isDate?: boolean; isBoolean?: boolean}> = ({
  label,
  value,
  isDate,
  isBoolean,
}) => {
  let displayValue = value;
  if (value === null || value === undefined) {
    displayValue = '-';
  } else if (isDate) {
    displayValue = format(new Date(value as string), 'PPpp');
  } else if (isBoolean) {
    displayValue = value ? 'Yes' : 'No';
  }

  return (
    <div className="flex flex-col bg-slate-50 p-3 rounded-md border border-slate-200">
      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</span>
      <span className="text-sm font-medium text-slate-900 mt-1">{displayValue as string}</span>
    </div>
  );
};

export const Webhooks: FC = observer(() => {
  const webhooksQuery = useOrganizationWebhook();
  const [submitting, setSubmitting] = useState(false);
  const [rotatedSecret, setRotatedSecret] = useState<string | null>(null);

  // We want validation, but need to dynamically allow new webhook events
  const defineWebhookEventMap = (existingEventConfig?: TWebhookEventMap): TWebhookEventMap => {
    const defaultEventConfig = WEBHOOK_EVENTS.reduce<TWebhookEventMap>((a, b) => {
      a[b] = false;
      return a;
    }, {} as TWebhookEventMap);

    return {
      ...defaultEventConfig,
      ...existingEventConfig,
    };
  };

  const form = useForm<SchemaType>({
    mode: 'all',
    resolver: zodResolver(schema),
    defaultValues: async () => {
      try {
        const data = (await getWebhooks(VerdocsEndpoint.getDefault())) as any;
        const webhookStatusByEvent = defineWebhookEventMap(data.events);
        return {
          ...data,
          events: webhookStatusByEvent,
          auth_method: (data.auth_method as any) || 'none', // Ensure default matches enum
        };
      } catch (err) {
        console.log('Unable to get webhook record: ', err);
        const defaultEvents = defineWebhookEventMap();
        return {
          events: defaultEvents,
          auth_method: 'none',
        };
      }
    },
  });

  const authMethod = useWatch({control: form.control, name: 'auth_method'});
  const webhookData = webhooksQuery.data;
  console.log('Webhook form render', {schema, webhookData, values: form.getValues()});

  const handleSubmit = (values: any) => {
    console.log('[Webhook] handleSubmit called with values:', JSON.stringify(values, null, 2));
    setSubmitting(true);
    setWebhooks(VerdocsEndpoint.getDefault(), values)
      .then((r) => {
        setSubmitting(false);
        console.log('Updated webhook', r);
        toast.success('Settings saved');
      })
      .catch((e) => {
        setSubmitting(false);
        toast.error('Error saving Webhook settings: ' + e.response?.data?.error || e.message);
      });
  };

  const handleResetSecret = async (e: React.MouseEvent) => {
    e.preventDefault();

    // TODO: Nicer dialog box
    if (!confirm('Are you sure you want to reset the HMAC secret? This will take effect immediately.')) return;

    rotateWebhookSecret(VerdocsEndpoint.getDefault())
      .then((r) => {
        console.log('Webhook secret reset result', r);
        setRotatedSecret(r.secret_key ?? null);
        toast.success('HMAC secret has been rotated successfully.');
        return webhooksQuery.refetch();
      })
      .catch((e) => {
        console.warn('Webhook secret reset error', e);
        toast.error('Error rotating HMAC secret: ' + (e.response?.data?.error || e.message));
      });
  };

  if (webhooksQuery.isLoading) {
    return (
      <div className="p-4 h-full overflow-y-scroll">
        <PageTitle>Webhooks</PageTitle>
        <div className="mt-4" />
        <Spinner mode="dark" />
      </div>
    );
  }

  return (
    <div className="">
      <PageTitle>
        Webhooks
        <FlexFill />
        <HelpDialog title="Webhooks">
          <p>
            Webhooks are server-to-server notifications that may be sent to a URL of your choice when certain events occur within your
            organization. Webhook events are sent via POST requests with a JSON payload containing details about the event.
          </p>
          <p>
            If you are interested in using Webhooks but not yet ready to provide an endpoint of your own, we recommend the excellent{' '}
            <a href="https://webhook.site/" target="_blank" rel="noopener noreferrer">
              Webhook.site
            </a>
            &nbsp; tool for testing and development. This is a free service that provides a unique URL for testing Webhooks, with a modest
            monthly fee for more advanced features.
          </p>
        </HelpDialog>
      </PageTitle>

      <div className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatusBox label="Active" value={webhookData?.active ?? false} isBoolean />
        <StatusBox label="Status" value={webhookData?.status ?? 'Unknown'} />
        <StatusBox label="Last Success" value={webhookData?.last_success ?? null} isDate />
        <StatusBox label="Last Failure" value={webhookData?.last_failure ?? null} isDate />
      </div>

      <FormProvider {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit, (errors) =>
            console.error('[Webhook] Form validation errors:', errors, 'Values:', form.getValues()),
          )}
          className="flex flex-col gap-8">
          <div className="flex flex-col gap-4">
            <h3 className="text-lg font-semibold text-slate-800">Configuration</h3>
            <Divider />

            <TextInput label="Webhook URL" name="url" placeholder="https://api.example.com/webhooks" />
            <CheckboxInput label="Active" name="active" explainer="Disable to pause delivery temporarily." />

            <SelectInput
              label="Authentication Method"
              name="auth_method"
              options={[
                {label: 'None', value: 'none'},
                {label: 'HMAC Signature', value: 'hmac'},
                {label: 'Client Credentials (OAuth2)', value: 'client_credentials'},
              ]}
            />

            {authMethod === 'hmac' && (
              <div className="flex flex-col gap-2 p-4 bg-slate-50 border border-slate-200 rounded-md">
                <span className="text-sm font-semibold text-slate-700">HMAC Configuration</span>
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <label className="text-xs text-slate-500 uppercase">Secret Key</label>
                    {rotatedSecret ? (
                      <div className="font-mono text-sm bg-white p-2 border border-green-400 rounded break-all select-all">
                        {rotatedSecret}
                      </div>
                    ) : (
                      <div className="font-mono text-sm bg-white p-2 border border-slate-300 rounded truncate">
                        {webhookData?.secret_key ? webhookData.secret_key.replace('...', '••••••••••••') : 'Not configured'}
                      </div>
                    )}
                  </div>
                  <div className="mt-5">
                    <VerdocsButton label="Reset Secret" variant="outline" size="small" onClick={handleResetSecret} />
                  </div>
                </div>
                {rotatedSecret ? (
                  <p className="text-xs text-amber-700 font-semibold mt-1">
                    ⚠️ Please make a note of this value and keep it in a safe place. It will not be displayed again.
                  </p>
                ) : (
                  <p className="text-xs text-slate-500 mt-1">
                    The secret key is used to sign the webhook payload. Use this to verify the authenticity of the request.
                  </p>
                )}
              </div>
            )}

            {authMethod === 'client_credentials' && (
              <div className="flex flex-col gap-4 p-4 bg-slate-50 border border-slate-200 rounded-md">
                <span className="text-sm font-semibold text-slate-700">Client Credentials Configuration</span>
                <TextInput label="Token Endpoint" name="token_endpoint" placeholder="https://auth.example.com/oauth/token" />
                <TextInput label="Client ID" name="client_id" placeholder="Client ID" />
                <TextInput label="Client Secret" name="client_secret" type="password" placeholder="Client Secret" />
                <TextInput label="Scope (optional)" name="scope" placeholder="e.g. webhooks:write openid" />
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-x-8 gap-y-4 mt-4">
            <div className="col-span-full">
              <h3 className="text-lg font-semibold text-slate-800">Events</h3>
              <Divider />
            </div>

            <div className="flex flex-col">
              <h4 className="font-medium text-slate-700 mb-2">Envelope Activity</h4>
              <CheckboxInput label="Created" name="events.envelope_created" />
              <CheckboxInput label="Updated" name="events.envelope_updated" />
              <CheckboxInput label="Completed" name="events.envelope_completed" />
              <CheckboxInput label="Canceled" name="events.envelope_canceled" />
              <CheckboxInput label="Expired" name="events.envelope_expired" />
            </div>

            <div className="flex flex-col">
              <h4 className="font-medium text-slate-700 mb-2">Template Activity</h4>
              <CheckboxInput label="Created" name="events.template_created" />
              <CheckboxInput label="Updated" name="events.template_updated" />
              <CheckboxInput label="Deleted" name="events.template_deleted" />
              <CheckboxInput label="Used" name="events.template_used" />
            </div>

            <div className="flex flex-col">
              <h4 className="font-medium text-slate-700 mb-2">Recipient Activity</h4>
              <CheckboxInput label="Invited" name="events.recipient_invited" />
              <CheckboxInput label="Reminded" name="events.recipient_reminded" />
              <CheckboxInput label="Opened" name="events.recipient_opened" />
              <CheckboxInput label="Submitted" name="events.recipient_submitted" />
              <CheckboxInput label="Updated" name="events.recipient_updated" />
              <CheckboxInput label="Delegated" name="events.recipient_delegated" />
              <CheckboxInput label="Auth Failed" name="events.recipient_auth_fail" />
              <CheckboxInput label="Disclosure Accepted" name="events.recipient_disclosure_accepted" />
              <CheckboxInput label="Docs Downloaded" name="events.recipient_docs_downloaded" />
              <CheckboxInput label="Invite Failed" name="events.recipient_invite_failed" />
              <CheckboxInput label="Declined" name="events.recipient_declined" />
            </div>

            <div className="flex flex-col">
              <h4 className="font-medium text-slate-700 mb-2">Usage Tracking</h4>
              <CheckboxInput label="KBA Event" name="events.kba_event" />
              <CheckboxInput label="Entitlement Used" name="events.entitlement_used" />
            </div>
          </div>

          <VerdocsButton type="submit" label="Update Settings" size="normal" disabled={submitting} />
        </form>
      </FormProvider>
    </div>
  );
});
